import { Injectable, inject, signal } from '@angular/core';
import { Subject, firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Global } from '@services/core/Global';
import { StorageService } from '@services/core/storage.service';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public sessionExpired$ = new Subject<boolean>();
  /** No hay cookie ni token: hay que mostrar el ingreso, no el aviso de sesión vencida. */
  public signedOut$ = new Subject<void>();
  /** Emite tras un refresh de sesión por ACCESS_CHANGED (menú/entidades/rol). */
  public sessionRefreshed$ = new Subject<void>();
  authMicrosoft = inject(MicrosoftAuthService);
  public userData = signal(null);
  public userPhoto = signal<string | null>(null);
  public sharingIdentity = signal({});
  public sharingEntity = signal({});
  public sharingEntities = signal<any[]>([]);
  public sharingLanguage = signal({});
  public sharingRole = signal({});
  /** Roles disponibles del usuario (user_role). */
  public sharingRoles = signal<any[]>([]);
  /** Menús precargados por roleId (string). */
  public sharingMenusByRole = signal<Record<string, any[]>>({});
  /** Entidades precargadas por roleId (string). */
  public sharingEntitiesByRole = signal<Record<string, any[]>>({});
  public sharingMenu = signal<any[]>([]);
  public sharingMenuPath = signal({});
  public token;
  public url: string;
  storageService = inject(StorageService);
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(private _http: HttpClient) {
    this.url = Global.url;
    this.token = this.storageService.getJsonValue('token');
  }

  processUserData(userData?: any) {
    if (userData) this.userData.set(userData);
  }

  /** Rol activo USER: alcance por socio, sin entidades. */
  isUserRole(role?: any): boolean {
    const current = role ?? this.sharingRole();
    if (this._isUserRoleName(current)) return true;
    const stored = this.storageService.getJsonValue('role');
    return this._isUserRoleName(stored);
  }

  private _isUserRoleName(role: any): boolean {
    const name = String(role?.name || '').trim().toUpperCase();
    const label = String(role?.displayName || role?.description || '').trim().toUpperCase();
    return name === 'USER' || name === 'USUARIO' || label === 'USER' || label === 'USUARIO';
  }

  processUserPhoto(arrayBuffer?: ArrayBuffer) {
    if (!arrayBuffer) return;
    const url = this.arrayBufferToUrl(arrayBuffer);
    this.userPhoto.set(url);
  }

  private arrayBufferToUrl(arrayBuffer: ArrayBuffer): string {
    const array = new Uint8Array(arrayBuffer);
    const blob = new Blob([array], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
  }

  changeEntity(params): Observable<any> {
    return this._http.put(this.url + 'V1/user/', params);
  }

  /** Cambia el rol con el que actúa el usuario (user.role_id) y alinea estado local. */
  changeRole(roleId: number): Observable<any> {
    return this._http.put(this.url + 'V1/users/active-role', { roleId: Number(roleId) }).pipe(
      tap((res: any) => this.applyServerUserRole(res))
    );
  }

  // Aplica rol activo y menu precargados
  applyActiveRole(role: any, skipEntity = false): boolean {
    if (!role?.id) return false;
    const key = String(role.id);
    const menu = (this.sharingMenusByRole() ?? {})[key];
    const entities = (this.sharingEntitiesByRole() ?? {})[key];
    this._applyRoleBasics(role);
    if (!skipEntity && entities) this._applyEntitiesForRole(entities);
    if (menu) this._applyMenu(menu);
    return !!menu;
  }

  private _applyRoleBasics(role: any): void {
    this.sharingRole.set(role);
    this.storageService.setJsonValue('role', role);
    const curr = (this.sharingIdentity() as any) ?? {};
    this.sharingIdentity.set({ ...curr, roleId: Number(role.id) });
  }

  // Aplica entidades asignadas al rol activo
  private _applyEntitiesForRole(entities: any[]): void {
    const list = entities ?? [];
    this.sharingEntities.set(list);
    const next = list.find((e) => e?.isActive !== false) ?? list[0] ?? {};
    this.sharingEntity.set(next);
  }

  private _applyMenu(menu: any): void {
    this.sharingMenu.set(menu);
    this.storageService.setJsonValue('menu', menu);
  }

  // Tras PUT active-role: aplica rol y menu sin mutar entidad prematuramente
  applyServerUserRole(res: any): void {
    if (!res) return;
    const role = res.role ?? null;
    const roleId = Number(res.roleId ?? role?.id);
    if (role?.id) this.applyActiveRole(role, true);
    this._syncRoleState(res, roleId);
    this._syncIdentity(res, roleId);
  }

  private _syncRoleState(res: any, roleId: number): void {
    const key = String(roleId);
    if (res.menu) {
      this.sharingMenu.set(res.menu);
      this.storageService.setJsonValue('menu', res.menu);
      this.sharingMenusByRole.update((m) => ({ ...m, [key]: res.menu }));
    }
  }

  private _syncIdentity(res: any, roleId: number): void {
    const curr = (this.sharingIdentity() as any) ?? {};
    this.sharingIdentity.set({
      ...curr,
      roleId: Number.isFinite(roleId) ? roleId : curr.roleId,
      id: res.id ?? curr.id,
      mail: res.mail ?? curr.mail,
      displayName: res.displayName ?? curr.displayName
    });
  }

  refreshBudgetVersion() {
    return this.authMicrosoft.decryptToken().pipe(tap(res => this.updateState(res)));
  }

  /**
   * Relee decrypt-token y actualiza menú/rol/entidades.
   * @returns false si el usuario perdió acceso o hubo error de sesión.
   */
  async refreshSessionAccess(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this._doRefreshSessionAccess().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async _doRefreshSessionAccess(): Promise<boolean> {
    try {
      const res: any = await firstValueFrom(this.authMicrosoft.decryptToken());
      if (res?.error) {
        this.sessionExpired$.next(true);
        return false;
      }
      this.updateState(res);
      this._ensureCurrentEntity();
      this.sessionRefreshed$.next();
      return true;
    } catch (err: any) {
      if (err?.error?.code === 'NO_SESSION') this.signedOut$.next();
      else this.sessionExpired$.next(true);
      return false;
    }
  }

  private _ensureCurrentEntity(): void {
    const current = this.sharingEntity() as any;
    const list = (this.sharingEntities() as any[]) || [];
    if (!current?.code) return;
    const currentInList = list.find(e => String(e?.code) === String(current.code));
    // Sigue activa → no tocar. Si ya no está o quedó inactiva → pasar a otra activa.
    if (currentInList && currentInList.isActive !== false) return;
    const next = list.find(e => e?.isActive !== false) || null;
    this.sharingEntity.set(next || {});
  }

  private updateState(res: any) {
    const u = res.user;
    this.processUserData(u.user);
    if (res.photo?.body?.data) this.processUserPhoto(res.photo.body.data);
    this.setMenuAndRole(u);
    this.sharingEntities.set(u.entities);
    this.sharingEntity.set(u.entity);
    this.sharingIdentity.set(u.identity);
  }

  private setMenuAndRole(u: any) {
    const roles = Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []);
    this.sharingRoles.set(roles);
    this.storageService.setJsonValue('roles', roles);

    const menusByRole = u.menusByRole && typeof u.menusByRole === 'object' ? u.menusByRole : {};
    this.sharingMenusByRole.set(menusByRole);
    this.storageService.setJsonValue('menusByRole', menusByRole);

    const entitiesByRole = u.entitiesByRole && typeof u.entitiesByRole === 'object' ? u.entitiesByRole : {};
    this.sharingEntitiesByRole.set(entitiesByRole);
    this.storageService.setJsonValue('entitiesByRole', entitiesByRole);

    this.sharingRole.set(u.role);
    this.storageService.setJsonValue('role', u.role);

    const activeId = u.role?.id != null ? String(u.role.id) : null;
    const menu = (activeId && menusByRole[activeId]) ? menusByRole[activeId] : (u.menu || []);
    this.sharingMenu.set(menu);
    this.storageService.setJsonValue('menu', menu);
  }
}
